import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import AppConst from '../apis/appconst';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import DataTable from 'react-data-table-component';
import ProcedureBillModal, { PATIENT_TYPES, normalizePatientType } from '../models/ProcedureBillModal';

const ProcedureBillList = () => {

    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [headerLogo, setHeaderLogo] = useState('');

    const [filters, setFilters] = useState({
        from: '',
        to: '',
        search_key: '',
        patient_type: ''
    });
    const filtersRef = useRef({ from: '', to: '', search_key: '', patient_type: '' });

    // modal control
    const [showModal, setShowModal] = useState(false);
    const [modalPatient, setModalPatient] = useState(null);   // null => standalone add
    const [modalBillId, setModalBillId] = useState(null);     // set => open that bill

    /* ============================================================
     *  HELPERS
     * ============================================================ */

    const getLocalYYYYMMDD = (d = new Date()) => {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const getStoredUser = () => {
        try {
            const s = sessionStorage.getItem('ldata') || localStorage.getItem('ldata');
            if (s) return JSON.parse(s);
        } catch (e) { /* ignore */ }
        return null;
    };

    const isAdminUser = (() => {
        const u = getStoredUser();
        return String(u?.role_id ?? u?.roleId ?? '') === '1';
    })();

    // "2026-10-29 13:04" -> "29-Oct-2026 01:04 PM"
    const formatDateForDisplay = (dateStr) => {
        if (!dateStr) return '';
        try {
            const date = new Date(String(dateStr).replace(' ', 'T'));
            if (isNaN(date.getTime())) return String(dateStr);

            const day = String(date.getDate()).padStart(2, '0');
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            let hours = date.getHours();
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12 || 12;
            const minutes = String(date.getMinutes()).padStart(2, '0');

            return `${day}-${months[date.getMonth()]}-${date.getFullYear()} ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
        } catch (e) {
            return String(dateStr);
        }
    };

    const typeBadgeColor = (t) => {
        switch (t) {
            case 'IPD': return '#0d6efd';
            case 'OPD': return '#198754';
            case 'EMERGENCY': return '#dc3545';
            case 'CASUALTY': return '#fd7e14';
            default: return '#6c757d';
        }
    };

    const money = (v) => Number(v || 0).toFixed(2);

    /* ============================================================
     *  DATA
     * ============================================================ */

    const fetchList = async (overrides = {}) => {
        const cur = filtersRef.current;
        const from = overrides.from !== undefined ? overrides.from : cur.from;
        const to = overrides.to !== undefined ? overrides.to : cur.to;
        const searchKey = overrides.search_key !== undefined ? overrides.search_key : cur.search_key;
        const pType = overrides.patient_type !== undefined ? overrides.patient_type : cur.patient_type;

        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('page_no', 'none');            // DataTables pages on the client
            fd.append('search_key', searchKey || '');
            if (from) fd.append('from', from);
            if (to) fd.append('to', to);
            if (pType) fd.append('patient_type', pType);

            const res = await axios.post(
                AppConst.BASE_URL + AppConst.getProcedureBillsListUrl,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (res?.data?.status) {
                setRecords(Array.isArray(res.data.records) ? res.data.records : []);
            } else {
                setRecords([]);
                console.warn('get_procedure_bills_list: status false', res?.data);
            }
        } catch (err) {
            console.error('fetchList error:', err);
            toast.error('Could not load procedure bills');
            setRecords([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchConfig = async () => {
        try {
            const res = await axios.post(`${AppConst.BASE_URL}${AppConst.getconfig}?config_type=lab`);
            if (res.data.status && res.data.header_logo) setHeaderLogo(res.data.header_logo);
        } catch (e) { /* ignore */ }
    };

    useEffect(() => {
        const today = getLocalYYYYMMDD();
        setFilters(prev => ({ ...prev, from: today, to: today }));
        filtersRef.current = { from: today, to: today, search_key: '', patient_type: '' };

        fetchList({ from: today, to: today, search_key: '', patient_type: '' });
        fetchConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ============================================================
     *  FILTERS
     * ============================================================ */

    const updateFilters = (updates) => {
        setFilters(prev => ({ ...prev, ...updates }));
        filtersRef.current = { ...filtersRef.current, ...updates };

        // dates and type reload from the server straight away;
        // the search box filters on the client (below)
        if (updates.from !== undefined || updates.to !== undefined || updates.patient_type !== undefined) {
            fetchList(filtersRef.current);
        }
    };

    const clearFilters = () => {
        const today = getLocalYYYYMMDD();
        const reset = { from: today, to: today, search_key: '', patient_type: '' };
        setFilters(reset);
        filtersRef.current = reset;
        fetchList(reset);
    };

    // client-side search so typing is instant
    const filteredRecords = useMemo(() => {
        const q = (filters.search_key || '').toLowerCase().trim();
        if (!q) return records;

        return records.filter(r => {
            const candidates = [
                r.patient_name,
                r.ipd_bill_uhid,
                r.patient_phone,
                r.ipd_no,
                r.procedure_bill_id,
                `pro-${r.procedure_bill_id}`,
                r.patient_type,
                r.remarks,
            ].filter(Boolean).map(x => String(x).toLowerCase());

            return candidates.some(c => c.includes(q));
        });
    }, [records, filters.search_key]);

    // footer totals for whatever is currently shown
    const totals = useMemo(() => {
        return filteredRecords.reduce((acc, r) => {
            const gross = Number(r.total_amount || 0);
            const disc = Number(r.discount_amount || 0);
            const orig = Number(r.original_final_amount || Math.max(0, gross - disc));
            const paid = Number(r.paid_amount || 0);
            acc.gross += gross;
            acc.discount += disc;
            acc.net += orig;
            acc.paid += paid;
            acc.due += Math.max(0, orig - paid);
            return acc;
        }, { gross: 0, discount: 0, net: 0, paid: 0, due: 0 });
    }, [filteredRecords]);

    /* ============================================================
     *  ACTIONS
     * ============================================================ */

    const openAddModal = () => {
        setModalPatient(null);      // standalone — patient entered/searched inside the modal
        setModalBillId(null);
        setShowModal(true);
    };

    const openBill = (row) => {
        // hand the modal enough to render the patient header
        setModalPatient({
            ipd_bill_id: row.ipd_bill_id,
            ipd_bill_uhid: row.ipd_bill_uhid,
            ipd_no: row.ipd_no,
            patient_type: row.patient_type,
            ipd_bill_patient_name: row.patient_name,
            ipd_bill_patient_age: row.patient_age,
            ipd_bill_patient_gender: row.patient_gender,
            ipd_bill_patient_phone: row.patient_phone,
            ipd_bill_patient_address: row.patient_address,
        });
        setModalBillId(row.procedure_bill_id);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setModalPatient(null);
        setModalBillId(null);
    };

    const handleDelete = async (row) => {
        if (!isAdminUser) {
            Swal.fire({ icon: 'info', title: "Can't Delete", text: 'Only admin can delete a procedure bill.' });
            return;
        }

        const confirm = await Swal.fire({
            title: 'Delete this procedure bill?',
            html: `<div style="text-align:left">
                     <p><strong>Bill No:</strong> PRO-${row.procedure_bill_id}</p>
                     <p><strong>Patient:</strong> ${row.patient_name || ''}</p>
                     <p><strong>Amount:</strong> ₹${money(row.original_final_amount || row.total_amount)}</p>
                   </div>
                   <p style="color:#dc3545;margin-top:8px">This action cannot be undone.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            confirmButtonText: 'Yes, Delete'
        });
        if (!confirm.isConfirmed) return;

        try {
            Swal.fire({ title: 'Deleting...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

            const fd = new FormData();
            fd.append('procedure_bill_id', row.procedure_bill_id);
            const u = getStoredUser();
            if (u) {
                fd.append('user_id', u.user_id || '');
                fd.append('user_name', `${u.fname || ''} ${u.lname || ''}`.trim());
                fd.append('user_role', u.role_id || '');
            }

            const res = await axios.post(
                AppConst.BASE_URL + 'opd_apis/delete_procedure_bill',
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            Swal.close();

            if (res.data?.status) {
                toast.success(res.data.message || 'Procedure bill deleted');
                fetchList();
            } else {
                toast.error(res.data?.message || 'Delete failed');
            }
        } catch (err) {
            Swal.close();
            console.error('delete error:', err);
            toast.error('Error deleting procedure bill');
        }
    };

    /* ============================================================
     *  TABLE
     * ============================================================ */

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: '#f1f5fb',
                color: '#405189',
                fontSize: '14px',
                fontWeight: '700',
                paddingLeft: '12px',
                paddingRight: '12px',
            }
        },
        rows: { style: { fontSize: '14px', minHeight: '54px' } },
        cells: { style: { paddingLeft: '12px', paddingRight: '12px', fontSize: '14px' } },
    };

    const columns = [
        {
            name: 'Action',
            cell: row => (
                <div className="d-flex gap-1">
                    <button type="button" className="btn btn-sm btn-outline-warning"
                        title="Open / Edit bill" onClick={() => openBill(row)}>
                        <i className="ri-pencil-fill"></i>
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-dark"
                        title="Open & print" onClick={() => openBill(row)}>
                        <i className="ri-printer-line"></i>
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-danger"
                        title={isAdminUser ? 'Delete' : 'Only admin can delete'}
                        style={{ opacity: isAdminUser ? 1 : 0.5, cursor: isAdminUser ? 'pointer' : 'not-allowed' }}
                        onClick={() => handleDelete(row)}>
                        <i className="ri-delete-bin-5-fill"></i>
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '170px'
        },
        {
            name: 'SN',
            selector: row => filteredRecords.findIndex(r => r.procedure_bill_id === row.procedure_bill_id) + 1,
            width: '70px'
        },
        {
            name: 'Bill No',
            selector: row => `PRO-${row.procedure_bill_id}`,
            sortable: true,
            width: '130px'
        },
        {
            name: 'Type',
            cell: row => {
                const t = normalizePatientType(row.patient_type);
                return t
                    ? <span className="badge" style={{ background: typeBadgeColor(t) }}>{t}</span>
                    : <span className="text-muted">-</span>;
            },
            selector: row => row.patient_type || '',
            sortable: true,
            width: '140px'
        },
        {
            name: 'Date & Time',
            selector: row => formatDateForDisplay(row.created_at || row.bill_date),
            sortable: true,
            width: '190px'
        },
        {
            name: 'UHID',
            selector: row => row.ipd_bill_uhid || '-',
            sortable: true,
            width: '140px'
        },
        {
            name: 'Patient Name',
            selector: row => row.patient_name || '-',
            sortable: true,
            width: '190px'
        },
        {
            name: 'Age/Gender',
            selector: row => `${row.patient_age || '-'} / ${row.patient_gender || '-'}`,
            width: '140px'
        },
        {
            name: 'Phone',
            selector: row => row.patient_phone || '-',
            width: '140px'
        },
        {
            name: 'Total',
            selector: row => Number(row.total_amount || 0),
            format: row => `₹${money(row.total_amount)}`,
            sortable: true,
            right: true,
            width: '120px'
        },
        {
            name: 'Discount',
            selector: row => Number(row.discount_amount || 0),
            format: row => `₹${money(row.discount_amount)}`,
            right: true,
            width: '120px'
        },
        {
            name: 'Net',
            selector: row => Number(row.original_final_amount || 0),
            format: row => `₹${money(row.original_final_amount)}`,
            sortable: true,
            right: true,
            width: '120px'
        },
        {
            name: 'Paid',
            selector: row => Number(row.paid_amount || 0),
            format: row => <span style={{ color: '#198754', fontWeight: 600 }}>₹{money(row.paid_amount)}</span>,
            right: true,
            width: '120px'
        },
        {
            name: 'Balance',
            selector: row => Number(row.remaining_amount || 0),
            format: row => {
                const bal = Number(row.remaining_amount || 0);
                return <span style={{ color: bal > 0 ? '#dc3545' : '#198754', fontWeight: 600 }}>₹{money(bal)}</span>;
            },
            sortable: true,
            right: true,
            width: '130px'
        },
        {
            name: 'Status',
            cell: row => {
                const st = row.payment_status || 'Pending';
                const cls = st === 'Paid' ? 'bg-success'
                    : st === 'Partial' ? 'bg-warning text-dark'
                        : 'bg-secondary';
                return <span className={`badge ${cls}`}>{st}</span>;
            },
            selector: row => row.payment_status || 'Pending',
            sortable: true,
            width: '120px'
        },
    ];

    /* ============================================================
     *  RENDER
     * ============================================================ */

    return (
        <div className="page-content">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="card">

                            {/* ===== HEADER ===== */}
                            <div className="card-header border-0">
                                <div className="row align-items-center gy-3">
                                    <div className="col-sm">
                                        <h2 style={{ color: '#405189', margin: 0 }}>Procedure Bills</h2>
                                    </div>
                                    <div className="col-sm-auto">
                                        <div className="d-flex gap-1 flex-wrap">
                                            <button type="button" className="btn btn-success add-btn"
                                                onClick={openAddModal}>
                                                <i className="ri-add-line align-bottom me-1"></i> Add Procedure Bill
                                            </button>
                                            <button type="button" className="btn btn-soft-primary"
                                                title="Refresh" onClick={() => fetchList()}>
                                                <i className="ri-refresh-line"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ===== FILTERS ===== */}
                            <div className="card-body border border-dashed border-end-0 border-start-0">
                                <form onSubmit={(e) => e.preventDefault()}>
                                    <div className="row g-3">
                                        <div className="col-md-2">
                                            <label className="form-label">From</label>
                                            <input type="date" className="form-control"
                                                value={filters.from}
                                                onChange={(e) => updateFilters({ from: e.target.value })} />
                                        </div>

                                        <div className="col-md-2">
                                            <label className="form-label">To</label>
                                            <input type="date" className="form-control"
                                                value={filters.to}
                                                onChange={(e) => updateFilters({ to: e.target.value })} />
                                        </div>

                                        <div className="col-md-2">
                                            <label className="form-label">Patient Type</label>
                                            <select className="form-control"
                                                value={filters.patient_type}
                                                onChange={(e) => updateFilters({ patient_type: e.target.value })}>
                                                <option value="">All Types</option>
                                                {PATIENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                        </div>

                                        <div className="col-md-2 d-flex align-items-end">
                                            <button type="button" className="btn btn-info" onClick={clearFilters}>
                                                <i className="ri-filter-off-line me-1"></i> Clear
                                            </button>
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label">Search</label>
                                            <div className="search-box">
                                                <input type="text" className="form-control search"
                                                    placeholder="Patient name / UHID / phone / bill no..."
                                                    value={filters.search_key}
                                                    onChange={(e) => updateFilters({ search_key: e.target.value })} />
                                                <i className="ri-search-line search-icon"></i>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>

                            {/* ===== TABLE ===== */}
                            <div className="card-body pt-3">
                                <div className="table-responsive table-card mb-1">
                                    <DataTable
                                        columns={columns}
                                        data={filteredRecords}
                                        progressPending={loading}
                                        pagination
                                        paginationPerPage={10}
                                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                        highlightOnHover
                                        responsive
                                        persistTableHead
                                        customStyles={customStyles}
                                        noHeader
                                        noDataComponent={
                                            <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>
                                                <i className="ri-file-list-3-line" style={{ fontSize: '40px', color: '#ccc' }}></i>
                                                <p style={{ marginTop: '10px' }}>No procedure bills found for the selected filters</p>
                                            </div>
                                        }
                                    />
                                </div>

                                {/* ===== TOTALS ===== */}
                                <div className="d-flex justify-content-between align-items-center flex-wrap mt-2">
                                    <div className="text-muted">
                                        <strong>Bills:</strong> {filteredRecords.length} &nbsp;|&nbsp;
                                        <strong>Total:</strong> ₹{money(totals.gross)} &nbsp;|&nbsp;
                                        <strong>Discount:</strong> ₹{money(totals.discount)} &nbsp;|&nbsp;
                                        <strong>Net:</strong> ₹{money(totals.net)} &nbsp;|&nbsp;
                                        <strong style={{ color: '#198754' }}>Collected:</strong> ₹{money(totals.paid)} &nbsp;|&nbsp;
                                        <strong style={{ color: '#dc3545' }}>Outstanding:</strong> ₹{money(totals.due)}
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* ===== PROCEDURE BILL MODAL ===== */}
            {showModal && (
                <ProcedureBillModal
                    patientData={modalPatient}
                    initialBillId={modalBillId}
                    image={headerLogo}
                    onClose={closeModal}
                    onSaved={() => fetchList()}
                />
            )}
        </div>
    );
};

export default ProcedureBillList;